import React, { useState, useEffect } from 'react';
import { getFranchiseAdmins, updateFranchiseAdminStatus, deleteFranchiseAdmin } from '../../../services/franchise.service';
import Swal from 'sweetalert2';
import { db } from '../../../../firebase';
import { doc, updateDoc } from 'firebase/firestore';

const FranchiseAdminList = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [showPassword, setShowPassword] = useState(false);
    const itemsPerPage = 10;

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const data = await getFranchiseAdmins();
            setAdmins(data);
        } catch (error) {
            console.error('Failed to fetch admins', error);
            Swal.fire('Error', 'Failed to load franchise admins', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (admin) => {
        setSelectedAdmin(admin);
        setShowPassword(false);
    };

    const closeDetails = () => {
        setSelectedAdmin(null);
        setShowPassword(false);
    };

    const handleToggleStatus = async (admin) => {
        const newStatus = admin.status === 'active' ? 'inactive' : 'active';
        try {
            await updateFranchiseAdminStatus(admin.id, newStatus);
            setAdmins(admins.map(a => a.id === admin.id ? { ...a, status: newStatus } : a));
            Swal.fire({
                title: 'Status Updated',
                text: `${admin.fullName}'s status changed to ${newStatus}.`,
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    const handleDelete = async (adminId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteFranchiseAdmin(adminId);
                setAdmins(admins.filter(a => a.id !== adminId));
                Swal.fire('Deleted!', 'Franchise admin has been deleted.', 'success');
            } catch (error) {
                Swal.fire('Error', 'Failed to delete admin', 'error');
            }
        }
    };

    const handleApprovePayout = async (adminId, requestId) => {
        try {
            const admin = admins.find(a => a.id === adminId);
            if (!admin) return;

            const updatedRequests = (admin.withdrawalRequests || []).map(req => 
                req.id === requestId ? { ...req, status: 'Paid' } : req
            );

            // Also update the withdrawn total
            const request = admin.withdrawalRequests.find(r => r.id === requestId);
            const payoutAmount = request ? request.amount : 0;
            const newWithdrawnTotal = (admin.withdrawnAmount || 0) + payoutAmount;

            const adminRef = doc(db, 'franchiseAdmins', adminId);
            await updateDoc(adminRef, {
                withdrawalRequests: updatedRequests,
                withdrawnAmount: newWithdrawnTotal
            });

            // Update local state
            setAdmins(admins.map(a => a.id === adminId ? { ...a, withdrawalRequests: updatedRequests, withdrawnAmount: newWithdrawnTotal } : a));
            if (selectedAdmin && selectedAdmin.id === adminId) {
                setSelectedAdmin({ ...selectedAdmin, withdrawalRequests: updatedRequests, withdrawnAmount: newWithdrawnTotal });
            }

            Swal.fire({
                title: 'Payout Approved!',
                text: 'The withdrawal request has been marked as Paid.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error("Error approving payout:", error);
            Swal.fire('Error', 'Failed to approve payout request.', 'error');
        }
    };

    if (loading) {
        return (
            <div className="admin-content-v2" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <p style={{ color: '#64748b' }}>Loading franchise admins...</p>
            </div>
        );
    }

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentAdmins = admins.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(admins.length / itemsPerPage);

    return (
        <div className="admin-content-v2">
            <div className="admin-header-v2">
                <div>
                    <h1 className="admin-title-v2">Franchise Admins</h1>
                    <p className="admin-subtitle-v2">Manage and view registered franchise admins</p>
                </div>
            </div>

            <div className="table-card-v2">
                <div className="table-responsive">
                    <table className="v2-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>State & City</th>
                                <th>Mobile</th>
                                <th>Joined</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {admins.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                        No franchise admins registered yet.
                                    </td>
                                </tr>
                            ) : (
                                currentAdmins.map(admin => (
                                    <tr key={admin.id}>
                                        <td>
                                            <div className="user-cell">
                                                <div className="user-avatar-small">
                                                    {admin.fullName ? admin.fullName.charAt(0).toUpperCase() : 'F'}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 500 }}>{admin.fullName || 'N/A'}</span>
                                                    <span style={{ fontSize: '12px', color: '#64748b' }}>{admin.email}</span>
                                                    <span style={{ fontSize: '11px', color: '#1a7a5e', marginTop: '3px', fontWeight: 600, background: '#f0fdf4', padding: '2px 6px', borderRadius: '4px', alignSelf: 'flex-start', border: '1px solid #d1fae5' }}>
                                                        Password: {admin.password || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="income-badge-v2">{admin.state || 'N/A'}, {admin.city || 'N/A'}</span></td>
                                        <td>{admin.mobile || 'N/A'}</td>
                                        <td>{admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : 'N/A'}</td>
                                        <td>
                                            <label className="switch-v2" title="Toggle Status">
                                                <input 
                                                    type="checkbox" 
                                                    checked={admin.status === 'active'} 
                                                    onChange={() => handleToggleStatus(admin)}
                                                />
                                                <span className="slider-v2 round"></span>
                                            </label>
                                            <span style={{ fontSize: '10px', display: 'block', marginTop: '4px', color: admin.status === 'active' ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                                                {admin.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            <div className="action-buttons-v2">
                                                <button 
                                                    className="icon-btn-v2 view" 
                                                    title="View Details"
                                                    onClick={() => handleViewDetails(admin)}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                </button>
                                                <button 
                                                    className="icon-btn-v2 delete" 
                                                    title="Delete Admin"
                                                    onClick={() => handleDelete(admin.id)}
                                                    style={{ color: '#ef4444', marginLeft: '8px' }}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="pagination-v2">
                        <button 
                            disabled={currentPage === 1} 
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            className="page-btn-v2"
                        >
                            Previous
                        </button>
                        <span className="page-info-v2">Page {currentPage} of {totalPages}</span>
                        <button 
                            disabled={currentPage === totalPages} 
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className="page-btn-v2"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Modal for Details */}
            {selectedAdmin && (
                <div className="modal-overlay" onClick={closeDetails}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Franchise Admin Details</h2>
                            <button className="close-btn" onClick={closeDetails}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {/* Stats Overview */}
                            <div className="modal-stats" style={{ gridColumn: '1 / -1', display: 'flex', gap: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.5rem' }}>
                                <div style={{ flex: 1, padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Total Referrals</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a7a5e' }}>{selectedAdmin.totalReferrals || 0}</div>
                                </div>
                                <div style={{ flex: 1, padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Franchise Revenue</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a7a5e' }}>₹{selectedAdmin.franchiseRevenue || 0}</div>
                                </div>
                                <div style={{ flex: 1, padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Diagnosis Conducted</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a7a5e' }}>{selectedAdmin.diagnosisConducted || 0}</div>
                                </div>
                            </div>

                            <div className="detail-group">
                                <label>Full Name</label>
                                <p>{selectedAdmin.fullName || 'N/A'}</p>
                            </div>
                            <div className="detail-group">
                                <label>Email Address</label>
                                <p>{selectedAdmin.email || 'N/A'}</p>
                            </div>
                                    <div className="detail-group">
                                        <label>Mobile Number</label>
                                        <p>{selectedAdmin.mobile || 'N/A'}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>Portal Password</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ 
                                                fontFamily: showPassword ? 'inherit' : 'monospace', 
                                                fontSize: showPassword ? 'inherit' : '1.1rem', 
                                                letterSpacing: showPassword ? 'normal' : '1.5px',
                                                fontWeight: 600,
                                                color: '#1e293b'
                                            }}>
                                                {showPassword ? (selectedAdmin.password || 'N/A') : '••••••••'}
                                            </span>
                                            <button 
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#64748b', display: 'flex', alignItems: 'center' }}
                                                title={showPassword ? "Hide Password" : "Show Password"}
                                            >
                                                {showPassword ? (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="detail-group">
                                        <label>Location</label>
                                        <p>{selectedAdmin.city}, {selectedAdmin.district}, {selectedAdmin.state} - {selectedAdmin.pincode}</p>
                                    </div>
                                    <div className="detail-group full-width">
                                        <label>Office Address</label>
                                        <p>{selectedAdmin.officeAddress || 'N/A'}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>Bank Name</label>
                                        <p>{selectedAdmin.bankName || 'N/A'}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>Account Holder</label>
                                        <p>{selectedAdmin.accountHolderName || 'N/A'}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>Account Number</label>
                                        <p>{selectedAdmin.accountNumber || 'N/A'}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>IFSC Code</label>
                                        <p>{selectedAdmin.ifscCode || 'N/A'}</p>
                                    </div>
                                    <div className="detail-group">
                                        <label>Registration Date</label>
                                        <p>{selectedAdmin.createdAt ? new Date(selectedAdmin.createdAt).toLocaleString() : 'N/A'}</p>
                                    </div>

                                    {/* Withdrawal Requests Section */}
                                    <div style={{ gridColumn: '1 / -1', marginTop: '1.5rem', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
                                        <h4 style={{ margin: '0 0 1rem 0', color: '#1e293b' }}>Withdrawal Requests</h4>
                                        {!selectedAdmin.withdrawalRequests || selectedAdmin.withdrawalRequests.length === 0 ? (
                                            <p style={{ color: '#64748b', fontSize: '0.9rem', fontStyle: 'italic', margin: 0 }}>No withdrawal requests submitted yet.</p>
                                        ) : (
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                            <th style={{ padding: '8px', textAlign: 'left', color: '#64748b' }}>Date</th>
                                                            <th style={{ padding: '8px', textAlign: 'left', color: '#64748b' }}>Amount</th>
                                                            <th style={{ padding: '8px', textAlign: 'left', color: '#64748b' }}>Status</th>
                                                            <th style={{ padding: '8px', textAlign: 'right', color: '#64748b' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedAdmin.withdrawalRequests.map((req, i) => (
                                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <td style={{ padding: '8px' }}>{new Date(req.date).toLocaleDateString()}</td>
                                                                <td style={{ padding: '8px', fontWeight: 600 }}>₹{req.amount.toLocaleString('en-IN')}</td>
                                                                <td style={{ padding: '8px' }}>
                                                                    <span style={{ 
                                                                        padding: '2px 6px', 
                                                                        borderRadius: '4px', 
                                                                        fontSize: '0.7rem', 
                                                                        fontWeight: 600,
                                                                        background: req.status === 'Paid' ? '#dcfce7' : '#fef9c3',
                                                                        color: req.status === 'Paid' ? '#166534' : '#854d0e'
                                                                    }}>
                                                                        {req.status}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '8px', textAlign: 'right' }}>
                                                                    {req.status === 'Pending' && (
                                                                        <button 
                                                                            onClick={() => handleApprovePayout(selectedAdmin.id, req.id)}
                                                                            style={{ background: '#1a7a5e', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}
                                                                        >
                                                                            Approve & Pay
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closeDetails}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .admin-content-v2 { padding: 2rem; }
                .table-card-v2 {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
                    overflow: hidden;
                    margin-top: 1.5rem;
                }
                .modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                    backdrop-filter: blur(4px);
                }
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    width: 90%;
                    max-width: 600px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    animation: slideUp 0.3s ease-out;
                }
                @keyframes slideUp {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .modal-header {
                    padding: 1.5rem;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .modal-header h2 {
                    margin: 0;
                    font-size: 1.25rem;
                    color: #1e293b;
                }
                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: #64748b;
                    transition: color 0.2s;
                }
                .close-btn:hover { color: #ef4444; }
                .modal-body {
                    padding: 1.5rem;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.25rem;
                }
                .detail-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }
                .detail-group.full-width {
                    grid-column: 1 / -1;
                }
                .detail-group label {
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #64748b;
                    font-weight: 600;
                }
                .detail-group p {
                    margin: 0;
                    font-size: 0.95rem;
                    color: #1e293b;
                    font-weight: 500;
                }
                .modal-footer {
                    padding: 1.25rem 1.5rem;
                    border-top: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: flex-end;
                }
                .btn-secondary {
                    background: #f1f5f9;
                    color: #475569;
                    border: none;
                    padding: 0.6rem 1.5rem;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .btn-secondary:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }

                /* Toggle Switch Styles */
                .switch-v2 {
                    position: relative;
                    display: inline-block;
                    width: 44px;
                    height: 24px;
                }
                .switch-v2 input { opacity: 0; width: 0; height: 0; }
                .slider-v2 {
                    position: absolute;
                    cursor: pointer;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background-color: #cbd5e1;
                    transition: .4s;
                }
                .slider-v2:before {
                    position: absolute;
                    content: "";
                    height: 18px; width: 18px;
                    left: 3px; bottom: 3px;
                    background-color: white;
                    transition: .4s;
                }
                input:checked + .slider-v2 { background-color: #10b981; }
                input:focus + .slider-v2 { box-shadow: 0 0 1px #10b981; }
                input:checked + .slider-v2:before { transform: translateX(20px); }
                .slider-v2.round { border-radius: 34px; }
                .slider-v2.round:before { border-radius: 50%; }
                
                .icon-btn-v2.delete:hover {
                    background: #fee2e2;
                }
                
                /* Pagination Styles */
                .pagination-v2 {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #e2e8f0;
                    background: #f8fafc;
                }
                .page-btn-v2 {
                    background: white;
                    border: 1px solid #cbd5e1;
                    padding: 0.5rem 1rem;
                    border-radius: 6px;
                    color: #475569;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .page-btn-v2:hover:not(:disabled) {
                    background: #f1f5f9;
                    color: #1e293b;
                    border-color: #94a3b8;
                }
                .page-btn-v2:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .page-info-v2 {
                    font-size: 0.9rem;
                    color: #64748b;
                    font-weight: 500;
                }
            `}</style>
        </div>
    );
};

export default FranchiseAdminList;
